import { colors, radius, elevation, transitions } from '../../utils/designSystem';

export function Button({
  children,
  onClick,
  disabled,
  className = "",
  variant = "primary",
  size = "md",
  fullWidth = true
}) {
  // Size variants
  const sizes = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-3 text-base",
    lg: "px-6 py-4 text-lg",
  };

  // Variant styles using design system
  const variants = {
    primary: {
      background: colors.primary.main,
      color: colors.primary.contrast,
      hoverBackground: colors.primary.dark,
    },
    accent: {
      background: colors.accent.main,
      color: colors.accent.contrast,
      hoverBackground: colors.accent.dark,
    },
    treasure: {
      background: `linear-gradient(135deg, ${colors.treasure.gold} 0%, ${colors.treasure.amber} 100%)`,
      color: colors.neutral.black,
      hoverBackground: colors.treasure.amber,
    },
    ghost: {
      background: 'transparent',
      color: colors.neutral[700],
      hoverBackground: colors.neutral[100],
    },
    outline: {
      background: 'transparent',
      color: colors.neutral[700],
      hoverBackground: colors.neutral[50],
      border: `1px solid ${colors.neutral[300]}`,
    },
    // Legacy support
    brass: {
      background: colors.primary.main,
      color: colors.primary.contrast,
      hoverBackground: colors.primary.dark,
    },
    black: {
      background: colors.neutral.black,
      color: colors.neutral.white,
      hoverBackground: colors.neutral[800],
    },
  };

  const currentVariant = variants[variant] || variants.primary;
  const currentSize = sizes[size] || sizes.md;

  const baseStyles = {
    borderRadius: radius.button,
    boxShadow: elevation.md,
    transition: transitions.default,
    fontWeight: '600',
    fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    border: currentVariant.border || 'none',
    ...(!disabled && {
      ':hover': {
        transform: 'translateY(-1px)',
        boxShadow: elevation.lg,
      },
      ':active': {
        transform: 'scale(0.99)',
      },
    }),
  };

  const style = {
    background: currentVariant.background,
    color: currentVariant.color,
    borderRadius: baseStyles.borderRadius,
    boxShadow: baseStyles.boxShadow,
    transition: baseStyles.transition,
    fontWeight: baseStyles.fontWeight,
    cursor: baseStyles.cursor,
    opacity: baseStyles.opacity,
    border: baseStyles.border,
  };

  const baseClasses = `
    ${fullWidth ? 'w-full' : 'inline-block'}
    ${currentSize}
    font-semibold
    transition-all
    duration-250
    active:scale-[0.99]
    disabled:opacity-50
    disabled:cursor-not-allowed
    hover:shadow-lg
    ${!disabled && 'hover:-translate-y-0.5'}
  `.trim().replace(/\s+/g, ' ');

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${className}`}
      style={style}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = currentVariant.hoverBackground;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = currentVariant.background;
        }
      }}
    >
      {children}
    </button>
  );
}
